import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import TriggerNodePreview from "../../features/sequence/node_previews/TriggerNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export enum TriggerType {
  OnStart = "onStart",
  OnEnd = "onEnd",
}

export const TRIGGER_PARALLEL_OUTPUT_HANDLE_ID = "trigger-parallel-output";

export interface ITrigger extends Interaction {
  triggerType: TriggerType;
}

export function getTriggerLabel(triggerType: TriggerType) {
  switch (triggerType) {
    case TriggerType.OnStart:
      return "On Take Start";
    case TriggerType.OnEnd:
      return "On Take End";
    default:
      return "Trigger";
  }
}

export function getTriggerDescription(triggerType: TriggerType) {
  switch (triggerType) {
    case TriggerType.OnStart:
      return "Runs when the take begins.";
    case TriggerType.OnEnd:
      return "Runs when the take ends.";
    default:
      return "Runs during the take lifecycle.";
  }
}

export const triggerInteraction = (): InteractionFactoryEntity => {
  const create = (): ITrigger => {
    const triggerType = TriggerType.OnStart;
    return {
      id: GetNewGuid(),
      name: getTriggerLabel(triggerType),
      type: InteractionType.Trigger,
      triggerType,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): ITrigger => {
    const trigger = interaction as ITrigger;
    return {
      ...trigger,
      name: getTriggerLabel(trigger.triggerType),
    };
  };

  const copy = (interaction: Interaction): ITrigger => {
    const trigger = interaction as ITrigger;
    return {
      id: GetNewGuid(),
      name: getTriggerLabel(trigger.triggerType),
      type: InteractionType.Trigger,
      triggerType: trigger.triggerType,
    };
  };

  const validate = (): string[] => {
    return [];
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
  ): SearchResultData[] => {
    return [];
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ): string[] => {
    const trigger = interaction as ITrigger;
    if (
      trigger.triggerType !== TriggerType.OnStart &&
      trigger.triggerType !== TriggerType.OnEnd
    ) {
      return ["Trigger type is required"];
    }

    return [];
  };

  return {
    View: <></>,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <TriggerNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
    FilterType: "Trigger",
  };
};
